/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/OrganizationManagement/index.tsx
 * PURPOSE: SC-006-ORG-STRUCTURE — Cơ cấu tổ chức (tree-table 5 cột + modal
 *          Thêm/Sửa in-page + ⁝ menu + Xuất/Nhập) đúng WIREFRAME-V0-MASTER-REGISTRY
 *          L806-978
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-07-30
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + BS-006/BS-008
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * SC6-1..SC6-9 remediation (2026-07-30, @coder_frontend) theo
 * temp/REVIEW-UI-DIVERGENCE-2026-07-30.md §8.3:
 * - SC6-1: tiêu đề "Cơ cấu tổ chức", bỏ subtitle "Per ADR-028".
 * - SC6-2: "+ Thêm đơn vị" mở modal [10] in-page (bỏ trang /new riêng); bỏ
 *   "Làm mới" + view toggle "Danh sách/Cây phân cấp" (registry: KHÔNG bảng phẳng).
 * - SC6-3: [Xuất ▼] .xlsx/.csv client-side (cùng pattern SC-007; backend chưa có
 *   endpoint export) — xuất cả cây gồm Mã đơn vị + Mã loại + Mã đơn vị cha.
 * - SC6-4: toolbar = [Tìm đơn vị…] + [Lọc loại ▼] (bind /tenant-admin/unit-types)
 *   + đếm "N đơn vị"; bỏ filter Cấp độ/Trạng thái.
 * - SC6-5: tree-table 5 cột (Tên đơn vị thụt + ▾▸ · Mã đơn vịⓘ · Loại · Trạng
 *   tháiⓘ · Thao tác ⁝) thay bảng phẳng 7 cột sort ↕.
 * - SC6-6: chip "● Hoạt động / ○ Ngừng" (bỏ "⏳ Chờ duyệt").
 * - SC6-7: ⁝ menu 4 mục + dialog styled [12] (xác nhận xoá) / [13] (chặn khi còn
 *   con hoặc 409 từ server); bỏ window.confirm.
 * - SC6-9: wording "tổ chức" → "đơn vị".
 * Data model backend 8 lớp KHÔNG đổi (Note A @architect) — UI render theo registry.
 *
 * @business-context
 * - Domain: Organization Structure, Multi-Tenant Architecture
 * - Impact: Bộ khung neo lịch làm việc + chấm công theo đơn vị
 * - Stakeholders: Tenant Admins
 *
 * @technical-architecture
 * - Layer: Presentation Layer
 * - Pattern: Tree-table + in-page modal + ⁝ row menu + client-side export
 * - Dependencies: React, useOrganizationManagement, /tenant-admin/unit-types, xlsx
 *
 * @cross-references
 * - Spec SSOT: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md (SC-006 L806-978)
 * - Review: temp/REVIEW-UI-DIVERGENCE-2026-07-30.md (§8.3 SC6-1..SC6-9)
 * - Hook: frontend/src/hooks/useOrganizationManagement.ts
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { OrganizationEntity, OrganizationCreate, getOrganizationTypeConfig } from '@/types/organization';
import {
  OrganizationModal,
  fetchUnitTypes,
  UNIT_TYPE_ORG_MAP,
  ORG_TYPE_TO_UNIT_CODE,
  type UnitType,
} from './components/OrganizationModal';
import { PortalMenu, type PortalMenuAnchor } from '@/components/common/PortalMenu';
import { PortalTooltip } from '@/components/common/PortalTooltip';
import { useOrganizationManagement } from '@/hooks/useOrganizationManagement';
import { apiClient } from '@/services/api/client';
import { API_BASE_URL } from '@/services/api/config';
import { BranchGeofenceModal, type BranchGeofenceTarget } from '@/pages/tenant-admin/BranchGeofenceModal';
import './organization-management.css';

/** Resolve API base URL from the canonical single source (services/api/config.ts). */
const getApiBase = (version: string) => `${API_BASE_URL}/api/${version}`;

// WP-8 SC-006 [15] — response preview/commit từ /tenant-admin/organizations/import/*
interface ImportPreviewRow {
  row: number;
  code: string;
  name: string;
  type_code: string;
  parent_code: string;
  ok: boolean;
  action: 'add' | 'update' | 'error';
  reason: string;
  // ISS-PILOT-15 (2026-08-09): true khi type_code chưa có trong danh mục —
  // dòng vẫn hợp lệ (ok=true), commit sẽ tự tạo Loại đơn vị mới.
  will_create_type?: boolean;
}

interface ImportPreview {
  total: number;
  valid: number;
  errors: number;
  error_rows: number[];
  rows: ImportPreviewRow[];
  // ISS-PILOT-15: mã Loại đơn vị mới, sẽ tự tạo khi bấm [Nhập N dòng hợp lệ]
  new_type_codes?: string[];
}

// BOT-027 (#758): toast xác nhận import phải giữ phân biệt Thêm/Cập nhật mà
// modal Xem trước NGAY TRƯỚC ĐÓ đã thể hiện rõ ("✔ Thêm" / "✔ Cập nhật",
// dòng ~891) — cộng gộp thành 1 số ("Đã nhập N đơn vị") khiến TA hiểu nhầm
// N đơn vị đều MỚI, trong khi tổng cây chỉ tăng đúng bằng phần "Thêm".
export const formatImportCommitToast = (
  imported: number,
  updated: number,
  createdTypes = 0
): string => {
  const base =
    imported > 0 && updated > 0
      ? `Đã thêm ${imported} đơn vị mới, cập nhật ${updated} đơn vị`
      : imported > 0
      ? `Đã thêm ${imported} đơn vị mới`
      : updated > 0
      ? `Đã cập nhật ${updated} đơn vị`
      : 'Đã nhập 0 đơn vị';
  return createdTypes > 0 ? `${base} + tạo ${createdTypes} Loại đơn vị mới` : base;
};

// Styles modal Nhập [14]/[15] + dialog [12]/[13] + toast — token var(--nqh-*)
const impStyles = {
  overlay: {
    position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: 'white', borderRadius: '12px', padding: '24px',
    maxWidth: '480px', width: '92%', maxHeight: '85vh', overflowY: 'auto' as const,
  } as React.CSSProperties,
  modalTitle: {
    margin: '0 0 16px', fontSize: '18px', fontWeight: 600,
    color: 'var(--nqh-gray-900, #1f2937)',
  } as React.CSSProperties,
  muted: {
    fontSize: '13px', color: 'var(--nqh-gray-500, #6b7280)', marginBottom: '8px',
  } as React.CSSProperties,
  errorBox: {
    marginTop: '12px', padding: '10px 12px', borderRadius: '8px', fontSize: '14px',
    background: 'var(--nqh-chili-050, #fef2f2)',
    border: '1px solid var(--nqh-chili-200, #fecaca)',
    color: 'var(--nqh-chili, #dc2626)',
  } as React.CSSProperties,
  modalFoot: {
    display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px',
  } as React.CSSProperties,
  okCell: { color: 'var(--nqh-herb-700, #15803d)', fontWeight: 600 } as React.CSSProperties,
  badCell: { color: 'var(--nqh-chili, #dc2626)', fontWeight: 600 } as React.CSSProperties,
  summary: {
    margin: '12px 0', fontSize: '14px', color: 'var(--nqh-gray-700, #374151)',
  } as React.CSSProperties,
  toast: {
    position: 'fixed' as const, bottom: '24px', left: '50%', transform: 'translateX(-50%)',
    background: 'var(--nqh-gray-900, #1f2937)', color: 'white', padding: '10px 20px',
    borderRadius: '8px', fontSize: '14px', zIndex: 1100, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  } as React.CSSProperties,
};

/** D10: chú thích qua icon ⓘ + tooltip microcopy.
 * 2026-08-25: đổi từ span[title] sang PortalTooltip + SVG — đồng bộ pattern
 * manager (ManagerStaffPage/ManagerAttendancePage/ManagerSchedulePage.tsx). */
const InfoIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={14} height={14} aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--nqh-gray-500)', verticalAlign: 'middle', marginLeft: '2px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

export const OrganizationManagement: React.FC = () => {
  const {
    organizations,
    loading,
    error,
    createOrganization,
    updateOrganization,
    refreshOrganizations,
    searchOrganizations,
  } = useOrganizationManagement();

  // BOT-010: cây tổ chức dùng chung (AnchorPicker, dropdown "Đơn vị cha"…) đọc
  // qua TanStack query ['organizations','tree',tenantId] với staleTime 5 phút
  // (useOrganizationTree.ts) — sau khi nhập hàng loạt phải invalidate, nếu
  // không TA phải F5 mới thấy đơn vị mới. Cùng pattern OrgCreateSidePanel.tsx:241.
  const queryClient = useQueryClient();

  // Modal [10] Thêm (gốc hoặc con) / [11] Sửa — in-page theo SC6-2
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<OrganizationEntity | null>(null);
  const [createParent, setCreateParent] = useState<OrganizationEntity | null>(null);

  // Toolbar SC6-4: [1] tìm đơn vị + [2] lọc loại (danh mục unit-types) + [3] đếm
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);

  // ⁝ menu + dialog [12]/[13] (SC6-7)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<OrganizationEntity | null>(null);
  const [blockDelete, setBlockDelete] = useState<OrganizationEntity | null>(null);
  const [deleting, setDeleting] = useState(false);

  // SC-028-BRANCH-GEOFENCE Điểm vào 1 (DD-0099 + BR-TENM-006 FR-012.v0): modal
  // dùng chung mở từ ⁝ trên node cấp Chi nhánh (organization_type === 'branch').
  const [geofenceTarget, setGeofenceTarget] = useState<BranchGeofenceTarget | null>(null);

  // SC6-3: [9] Xuất ▼ → menu [9m]
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ISS-PILOT-33 (2026-08-17): title "Cơ cấu tổ chức" không sticky → biến mất
  // khi trang cuộn dọc (đo được pageHeight 960 > viewport 900, xem
  // docs/05-test/13-test-issues/RELEASE-V0-ISSUES-CONSOLIDATED-2026-08-09.md §95
  // "Phát hiện 1" — đây MỚI là trang thật sự bị bug, không phải TenantSettings.tsx).
  // isNarrow: sticky CHỈ bật ở ≥1024px, cùng breakpoint (max-width: 1023px) với
  // TenantAdminLayout.tsx ☰ (position:sticky, top:8px, zIndex:40) — dưới 1024px
  // giữ hành vi cũ (không sticky) để tránh lặp lại regression "hamburger đè title"
  // đã gặp khi fix TenantSettings.tsx (xem §95 "Phát hiện 2").
  const [isNarrow, setIsNarrow] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth < 1024
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    setIsNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ── WP-8 SC-006 [8] Nhập → modal [14] chọn tệp → [15] xem trước → commit ──
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [committing, setCommitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const importFileRef = useRef<HTMLInputElement | null>(null);

  // SC6-4: danh mục loại đơn vị (BS-008) cho filter + cột Loại + modal
  useEffect(() => {
    fetchUnitTypes()
      .then(setUnitTypes)
      .catch(() => setUnitTypes([])); // fallback nhãn legacy getOrganizationTypeConfig
  }, []);

  // Toast tự ẩn sau 4s (pattern window.nqhToast trong mockup common.js)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Đóng ⁝ menu / menu Xuất khi click ra ngoài
  useEffect(() => {
    if (!menuOpenId && !exportMenuOpen) return;
    const close = () => { setMenuOpenId(null); setExportMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpenId, exportMenuOpen]);

  const openImportModal = useCallback(() => {
    setImportFile(null);
    setImportError(null);
    setShowImportModal(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreview(null);
    setImportFile(null);
  }, []);

  const postImportFile = useCallback(
    async (endpoint: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      // Content-Type undefined → axios tự gắn multipart boundary
      return apiClient.post(endpoint, formData, {
        baseURL: getApiBase('v2'),
        headers: { 'Content-Type': undefined },
      });
    },
    []
  );

  const extractError = useCallback((err: unknown, fallback: string): string => {
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
  }, []);

  // [14] → [Xem trước] → POST import/preview (không ghi DB) → mở modal [15]
  // BOT-008: modal [14] chỉ có text liệt kê cột, không có file mẫu để tải —
  // TA phải tự đoán format + tra thủ công "Mã loại" hợp lệ. Sinh template
  // client-side (cùng pattern với handleExport bên dưới, dùng thư viện XLSX
  // đã có sẵn) — sheet 1: đúng 4 cột yêu cầu + 1 dòng ví dụ; sheet 2: Mã loại
  // hợp lệ của tenant hiện tại (unitTypes đã load sẵn trong state trang này).
  const handleDownloadImportTemplate = useCallback(() => {
    const activeTypes = unitTypes.filter((t) => t.is_active);
    const wsMain = XLSX.utils.json_to_sheet([
      {
        'Mã đơn vị': 'VD-001',
        'Tên đơn vị': 'Chi nhánh mẫu',
        'Mã loại': activeTypes[0]?.code || '',
        'Mã đơn vị cha': '',
      },
    ]);
    const wsTypes = XLSX.utils.json_to_sheet(
      activeTypes.map((t) => ({ 'Mã loại': t.code, 'Tên loại': t.name }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsMain, 'Nhap co cau to chuc');
    XLSX.utils.book_append_sheet(wb, wsTypes, 'Ma loai hop le');
    XLSX.writeFile(wb, 'template-nhap-co-cau-to-chuc.xlsx');
  }, [unitTypes]);

  const handleImportPreview = useCallback(async () => {
    if (!importFile) return;
    setPreviewing(true);
    setImportError(null);
    try {
      const res = await postImportFile('/tenant-admin/organizations/import/preview', importFile);
      setPreview(res.data);
      setShowImportModal(false);
    } catch (err) {
      setImportError(extractError(err, 'Không đọc được tệp. Kiểm tra lại định dạng.'));
    } finally {
      setPreviewing(false);
    }
  }, [importFile, postImportFile, extractError]);

  // [15] → [Nhập N dòng hợp lệ] → POST import/commit → toast + reload cây
  const handleImportCommit = useCallback(async () => {
    if (!importFile || !preview) return;
    setCommitting(true);
    try {
      const res = await postImportFile('/tenant-admin/organizations/import/commit', importFile);
      const createdTypes = res.data?.created_types ?? 0;
      setPreview(null);
      setImportFile(null);
      setToast(formatImportCommitToast(res.data?.imported ?? 0, res.data?.updated ?? 0, createdTypes));
      refreshOrganizations();
      // BOT-010: bỏ tiền tố tenantId → invalidate mọi biến thể của query cây.
      queryClient.invalidateQueries({ queryKey: ['organizations', 'tree'] });
      // Danh mục Loại đơn vị vừa có thêm dòng mới — nạp lại để cột "Loại" +
      // dropdown modal Thêm/Sửa đơn vị thấy đúng ngay, không cần F5 thủ công.
      fetchUnitTypes().then(setUnitTypes).catch(() => {});
    } catch (err) {
      setPreview(null);
      setImportFile(null);
      setToast(extractError(err, 'Lỗi nhập cơ cấu — thử lại'));
    } finally {
      setCommitting(false);
    }
  }, [importFile, preview, postImportFile, extractError, refreshOrganizations, queryClient]);

  /** Mã loại của 1 node (HOLDING/OPCO/BRANCH/WORKAREA/custom) — so khớp filter
   *
   * 2026-08-19 (BUG THẬT — user báo "Lỗi ứng dụng: Cannot read properties of
   * null (reading 'toUpperCase')" khi mở /tenant-admin/organizations): dữ
   * liệu thật có ít nhất 1 dòng organization_type = NULL ("NQH Demo Branch
   * Q1") — fallback `org.organization_type.toUpperCase()` crash cả trang
   * (error boundary) cho MỌI tài khoản Tenant Admin, không riêng tài khoản
   * nào. OrganizationModal.tsx cùng feature đã tự nhận diện đúng rủi ro này
   * (comment "map miss + fallback .toUpperCase() trên null ⇒ crash error
   * boundary") và guard đúng — chỗ này thì chưa. Guard tương tự: coi
   * organization_type rỗng là "UNKNOWN" thay vì crash.
   *
   * 2026-08-26: nhánh `main` độc lập vá đúng cùng bug này khi verify bot-006/
   * 008 trên tenant rel-v0-e2e-shared — xác nhận đây là bug thật, không phải
   * suy đoán. Giữ fallback "UNKNOWN" (rõ ràng hơn chuỗi rỗng khi hiển thị). */
  const unitCodeOf = useCallback(
    (org: OrganizationEntity): string =>
      ORG_TYPE_TO_UNIT_CODE[org.organization_type] ?? org.organization_type?.toUpperCase() ?? 'UNKNOWN',
    []
  );

  /** Tên loại hiển thị cột Loại — từ danh mục unit-types, fallback nhãn legacy VI */
  const typeNameByOrgType = useMemo(() => {
    const map = new Map<string, string>();
    unitTypes.forEach((t) => {
      const orgType = UNIT_TYPE_ORG_MAP[t.code]?.orgType ?? t.code.toLowerCase();
      map.set(orgType, t.name);
    });
    return map;
  }, [unitTypes]);

  const typeLabel = useCallback(
    (org: OrganizationEntity): string =>
      typeNameByOrgType.get(org.organization_type) ??
      getOrganizationTypeConfig(org.organization_type).labelVi,
    [typeNameByOrgType]
  );

  const filteredOrganizations = useMemo(() => {
    let result = organizations;
    if (searchQuery) result = searchOrganizations(searchQuery);
    if (typeFilter) result = result.filter((org) => unitCodeOf(org) === typeFilter);
    return result;
  }, [organizations, searchQuery, typeFilter, searchOrganizations, unitCodeOf]);

  // FIX-H: full id→entity map for ancestor traversal
  const byId = useMemo(
    () => new Map(organizations.map((o) => [o.id, o])),
    [organizations]
  );

  const hasActiveFilter = !!(typeFilter || searchQuery);

  // FIX-H: tree receives matching nodes + all their ancestors
  const treeOrganizations = useMemo(() => {
    if (!hasActiveFilter) return organizations;
    const toInclude = new Set(filteredOrganizations.map((o) => o.id));
    filteredOrganizations.forEach((org) => {
      let parentId = org.parent_id;
      while (parentId) {
        toInclude.add(parentId);
        parentId = byId.get(parentId)?.parent_id ?? null;
      }
    });
    return organizations.filter((o) => toInclude.has(o.id));
  }, [organizations, filteredOrganizations, hasActiveFilter, byId]);

  // FIX-H: IDs that directly match filter (for highlighting)
  const filteredIds = useMemo(
    () => (hasActiveFilter ? new Set(filteredOrganizations.map((o) => o.id)) : null),
    [hasActiveFilter, filteredOrganizations]
  );

  // ── Handlers: modal [10]/[11] in-page (SC6-2) ──
  const handleOpenCreateRoot = useCallback(() => {
    setEditingOrg(null);
    setCreateParent(null);
    setIsModalOpen(true);
  }, []);

  const handleAddChild = useCallback((parent: OrganizationEntity) => {
    setEditingOrg(null);
    setCreateParent(parent);
    setIsModalOpen(true);
  }, []);

  const handleOpenEditModal = useCallback((org: OrganizationEntity) => {
    setEditingOrg(org);
    setCreateParent(null);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingOrg(null);
    setCreateParent(null);
  }, []);

  const handleSubmitOrganization = useCallback(
    async (data: OrganizationCreate & { is_active?: boolean }): Promise<void> => {
      if (editingOrg) {
        await updateOrganization(editingOrg.id, {
          name: data.name,
          organization_type: data.organization_type,
          level: data.level,
          parent_id: data.parent_id,
          ...(data.is_active !== undefined ? { is_active: data.is_active } : {}),
        });
      } else {
        await createOrganization(data);
      }
      handleCloseModal();
      // SAVED (registry L983): toast "Đã lưu đơn vị" + làm mới cây
      setToast('Đã lưu đơn vị');
    },
    [editingOrg, createOrganization, updateOrganization, handleCloseModal]
  );

  // ⁝ menu: Ngừng hoạt động / Kích hoạt — xác nhận nhanh (registry L996)
  const handleToggleStatus = useCallback(
    async (org: OrganizationEntity) => {
      try {
        await updateOrganization(org.id, { is_active: !org.is_active });
      } catch {
        setToast('Thao tác không thành công. Thử lại');
      }
    },
    [updateOrganization]
  );

  // ⁝ menu: "Cấu hình vị trí (Geofence)" — chỉ node cấp Chi nhánh (registry §SC-028
  // Điểm vào 1). Modal dùng chung PUT /organizations/{org_id}/geofence.
  const handleConfigureGeofence = useCallback((org: OrganizationEntity) => {
    setMenuOpenId(null);
    setGeofenceTarget({ orgId: org.id, name: org.name, code: org.code });
  }, []);

  // ⁝ menu: Xoá — BS-006-R06 CHẶN khi còn con (client check) → dialog [13];
  // còn người dùng gán vào → server 409 → dialog [13] (SC6-7)
  const handleDeleteRequest = useCallback(
    (org: OrganizationEntity) => {
      const hasChildren = organizations.some((o) => o.parent_id === org.id);
      if (hasChildren) setBlockDelete(org);
      else setConfirmDelete(org);
    },
    [organizations]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/organizations/${confirmDelete.id}`);
      setConfirmDelete(null);
      refreshOrganizations();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const target = confirmDelete;
      setConfirmDelete(null);
      if (status === 409) setBlockDelete(target);
      else setToast('Xoá không thành công. Thử lại');
    } finally {
      setDeleting(false);
    }
  }, [confirmDelete, refreshOrganizations]);

  // ── SC6-3: [9] Xuất ▼ → menu [9m] — xuất cả cây (gồm Mã đơn vị + Mã loại) ──
  const handleExport = useCallback(
    (format: 'xlsx' | 'csv') => {
      setExporting(true);
      try {
        // DFS cây để giữ thứ tự phân cấp; cột khớp định dạng Nhập [14] (round-trip)
        const rows: Array<Record<string, string>> = [];
        const walk = (parentId: string | null) => {
          organizations
            .filter((o) => (parentId === null ? !o.parent_id : o.parent_id === parentId))
            .sort((a, b) => (a.level || 1) - (b.level || 1) || a.name.localeCompare(b.name, 'vi'))
            .forEach((o) => {
              rows.push({
                'Mã đơn vị': o.code,
                'Tên đơn vị': o.name,
                'Mã loại': unitCodeOf(o),
                'Mã đơn vị cha': (o.parent_id && byId.get(o.parent_id)?.code) || '',
              });
              walk(o.id);
            });
        };
        walk(null);
        const stamp = new Date().toISOString().slice(0, 10);
        if (format === 'xlsx') {
          const ws = XLSX.utils.json_to_sheet(rows);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Co cau');
          XLSX.writeFile(wb, `co-cau-to-chuc-${stamp}.xlsx`);
        } else {
          const headers = ['Mã đơn vị', 'Tên đơn vị', 'Mã loại', 'Mã đơn vị cha'];
          const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
          const csv =
            '\uFEFF' +
            [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h] ?? '')).join(','))].join('\r\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `co-cau-to-chuc-${stamp}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        }
        setExportMenuOpen(false);
      } catch {
        // EXPORT-ING (registry L986): lỗi → toast "Xuất không thành công. Thử lại"
        setToast('Xuất không thành công. Thử lại');
      } finally {
        setExporting(false);
      }
    },
    [organizations, byId, unitCodeOf]
  );

  return (
    // 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout): bỏ maxWidth
    // 1200px + margin:auto — cap cố định để lại lề chết 240px MỖI bên ở
    // 1920px (đo thật), không co giãn theo bề rộng màn hình.
    <div className="org-management-page" style={{ padding: '24px' }}>
      {/* [7] Tiêu đề vùng nội dung + [8] Nhập + [9] Xuất ▼ + [4] + Thêm đơn vị (D3, D8) */}
      {/* ISS-PILOT-33: sticky để title không biến mất khi trang cuộn dọc (cây tổ
          chức nhiều cấp thường cao hơn viewport). marginTop:-24 huỷ padding-top
          24px của div bọc ngoài (org-management-page) rồi paddingTop:24 tái lập
          đúng khoảng cách đó BÊN TRONG chính div này — giữ nguyên tổng chiều cao
          khi chưa cuộn. background:#f3f4f6 khớp đúng nền TenantAdminLayout.tsx
          (flex container bọc sidebar+main, div này tự nó KHÔNG có background
          riêng nên nền thật đằng sau là #f3f4f6 xuyên qua) để che nội dung cuộn
          qua dưới. zIndex:10 = đúng giá trị đã dùng cho TenantSettings.tsx (đủ
          nổi trên nội dung tree-table, thấp hơn mọi modal zIndex:1000). Sticky
          CHỈ bật ≥1024px (isNarrow=false) — xem khai báo isNarrow phía trên. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          ...(isNarrow
            ? {}
            : {
                position: 'sticky' as const,
                top: 0,
                zIndex: 10,
                background: '#f3f4f6',
                marginTop: '-24px',
                paddingTop: '24px',
              }),
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--nqh-gray-900, #111827)', margin: 0 }}>
          Cơ cấu tổ chức
        </h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={openImportModal} className="org-btn org-btn-secondary">
            Nhập
          </button>
          {/* SC6-3: [9] Xuất ▼ → menu [9m] */}
          <div style={{ position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
            <button
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={exporting}
              className="org-btn org-btn-secondary"
            >
              {exporting ? 'Đang xuất…' : 'Xuất ▼'}
            </button>
            {exportMenuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: '4px', zIndex: 100,
                background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: '160px', overflow: 'hidden',
              }}>
                <button onClick={() => handleExport('xlsx')} style={menuItemStyle}>
                  Xuất Excel (.xlsx)
                </button>
                <button onClick={() => handleExport('csv')} style={menuItemStyle}>
                  Xuất CSV (.csv)
                </button>
              </div>
            )}
          </div>
          <button onClick={handleOpenCreateRoot} className="org-btn org-btn-primary">
            + Thêm đơn vị
          </button>
        </div>
      </div>

      {error && (
        <div className="org-error-banner" style={{
          background: 'var(--nqh-chili-100)', color: 'var(--nqh-chili-600)',
          padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span>⚠️ {error}</span>
          <button onClick={() => refreshOrganizations()} style={{
            background: 'var(--nqh-chili-500)', color: 'white',
            border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer',
          }}>Thử lại</button>
        </div>
      )}

      {/* Toolbar 1 hàng ngang (D4): [1] tìm đơn vị + [2] lọc loại + [3] đếm đẩy phải */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Tìm đơn vị…"
          className="org-search-input"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="org-filter-select"
          aria-label="Lọc loại"
        >
          <option value="">Tất cả loại</option>
          {unitTypes.filter((t) => t.is_active).map((t) => (
            <option key={t.id} value={t.code}>{t.name}</option>
          ))}
        </select>
        <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: '14px', color: 'var(--nqh-gray-500, #6b7280)' }}>
          {filteredOrganizations.length} đơn vị
        </span>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--nqh-gray-500)' }}>
          <p>Đang tải...</p>
        </div>
      )}

      {/* [5] Cây tổ chức (tree-table) — 5 cột, KHÔNG bảng phẳng song song (registry L794) */}
      {!loading && (
        <div className="org-data-table-container">
          <TreeTable
            organizations={treeOrganizations}
            filteredIds={filteredIds}
            typeLabel={typeLabel}
            unitCodeOf={unitCodeOf}
            menuOpenId={menuOpenId}
            setMenuOpenId={setMenuOpenId}
            onAddChild={handleAddChild}
            onEdit={handleOpenEditModal}
            onToggleStatus={handleToggleStatus}
            onDelete={handleDeleteRequest}
            onConfigureGeofence={handleConfigureGeofence}
          />
          {/* EMPTY (registry L978) */}
          {organizations.length === 0 && (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: 'var(--nqh-gray-500)' }}>
                Chưa có đơn vị nào. Bấm + Thêm đơn vị để tạo.
              </p>
            </div>
          )}
          {organizations.length > 0 && filteredOrganizations.length === 0 && (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <p style={{ fontSize: '14px', color: 'var(--nqh-gray-500)' }}>
                Không có đơn vị nào khớp bộ lọc.
              </p>
            </div>
          )}
        </div>
      )}

      <OrganizationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitOrganization}
        organization={editingOrg}
        parentOrganization={createParent}
        organizations={organizations}
        unitTypes={unitTypes}
      />

      {/* ══════════ SC6-7: [12] DIALOG xác nhận Xoá — giữa màn ~440px ══════════ */}
      {confirmDelete && (
        <div style={impStyles.overlay} role="dialog" aria-modal="true" aria-labelledby="delTitle">
          <div style={{ ...impStyles.modal, maxWidth: '440px' }}>
            <h2 style={impStyles.modalTitle} id="delTitle">Xoá đơn vị?</h2>
            <p style={{ fontSize: '14px', color: 'var(--nqh-gray-600, #4b5563)', margin: 0 }}>
              Đơn vị "{confirmDelete.name}" ({confirmDelete.code}) sẽ bị xoá khỏi cơ cấu tổ chức.
              Thao tác này không thể hoàn tác.
            </p>
            <div style={impStyles.modalFoot}>
              <button
                type="button"
                className="org-btn org-btn-secondary"
                disabled={deleting}
                onClick={() => setConfirmDelete(null)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="org-btn org-btn-primary"
                style={{ background: 'var(--nqh-chili, #dc2626)' }}
                disabled={deleting}
                onClick={handleDeleteConfirm}
              >
                {deleting ? 'Đang xoá…' : 'Xoá đơn vị'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ SC6-7: [13] DIALOG chặn Xoá — còn con hoặc còn người dùng ══════════ */}
      {blockDelete && (
        <div style={impStyles.overlay} role="dialog" aria-modal="true" aria-labelledby="blockDelTitle">
          <div style={{ ...impStyles.modal, maxWidth: '440px' }}>
            <h2 style={impStyles.modalTitle} id="blockDelTitle">Không thể xoá đơn vị</h2>
            <p style={{ fontSize: '14px', color: 'var(--nqh-gray-600, #4b5563)', margin: 0 }}>
              Đơn vị "{blockDelete.name}" ({blockDelete.code}) còn đơn vị con hoặc người dùng
              đang gán vào. Hãy chuyển/xoá chúng trước khi xoá đơn vị này.
            </p>
            <div style={impStyles.modalFoot}>
              <button type="button" className="org-btn org-btn-primary" onClick={() => setBlockDelete(null)}>
                Đã hiểu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ WP-8 SC-006 [14] MODAL "Nhập cơ cấu tổ chức" — bước 1 chọn tệp ══════════ */}
      {showImportModal && (
        <div style={impStyles.overlay} role="dialog" aria-modal="true" aria-labelledby="importTitle">
          <div style={impStyles.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={impStyles.modalTitle} id="importTitle">Nhập cơ cấu tổ chức</h2>
              {/* SC6-10 (2026-07-30): D6 — modal có [✕] góc trên (registry L920) */}
              <button
                type="button"
                aria-label="Đóng"
                onClick={() => setShowImportModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--nqh-gray-500, #6b7280)', padding: '2px' }}
              >
                ✕
              </button>
            </div>
            <div style={impStyles.muted}>Chọn tệp .xlsx hoặc .csv</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '4px 0 12px' }}>
              <input
                ref={importFileRef}
                type="file"
                accept=".xlsx,.csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  setImportFile(e.target.files?.[0] ?? null);
                  setImportError(null);
                }}
              />
              <button
                type="button"
                className="org-btn org-btn-secondary"
                onClick={() => importFileRef.current?.click()}
              >
                Chọn tệp…
              </button>
              <span style={{ fontSize: '14px', color: 'var(--nqh-gray-700, #374151)' }}>
                {importFile?.name || ''}
              </span>
            </div>
            <div style={impStyles.muted}>
              Cột yêu cầu: Mã đơn vị · Tên đơn vị · Mã loại · Mã đơn vị cha (rỗng = gốc)
            </div>
            <button
              type="button"
              className="org-btn org-btn-secondary"
              style={{ marginTop: '4px', marginBottom: '8px' }}
              onClick={handleDownloadImportTemplate}
            >
              Tải template
            </button>
            {importError && <div style={impStyles.errorBox}>{importError}</div>}
            <div style={impStyles.modalFoot}>
              <button
                type="button"
                className="org-btn org-btn-secondary"
                onClick={() => setShowImportModal(false)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="org-btn org-btn-primary"
                onClick={handleImportPreview}
                disabled={!importFile || previewing}
              >
                {previewing ? 'Đang xem trước…' : 'Xem trước'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ WP-8 SC-006 [15] MODAL "Xem trước nhập" — bảng 6 cột ══════════ */}
      {preview && (
        <div style={impStyles.overlay} role="dialog" aria-modal="true" aria-labelledby="previewTitle">
          <div style={{ ...impStyles.modal, maxWidth: '740px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={impStyles.modalTitle} id="previewTitle">
                Xem trước nhập — {preview.total} dòng ({preview.valid} hợp lệ · {preview.errors} lỗi)
              </h2>
              {/* SC6-10 (2026-07-30): D6 — modal có [✕] góc trên (registry L933) */}
              <button
                type="button"
                aria-label="Đóng"
                onClick={closePreview}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--nqh-gray-500, #6b7280)', padding: '2px' }}
              >
                ✕
              </button>
            </div>
            {/* ISS-PILOT-15 (2026-08-09, Hang Le chốt): banner tổng hợp Loại đơn vị
                MỚI sẽ tự tạo — TA phải thấy rõ TRƯỚC khi bấm [Nhập], không chỉ ẩn
                trong từng dòng. Bấm Nhập = xác nhận cả phần tạo Loại đơn vị này. */}
            {!!preview.new_type_codes?.length && (
              <div style={{
                background: 'var(--nqh-coffee-50, #fdf6ef)', border: '1px solid var(--nqh-coffee-200, #f3d9b1)',
                borderRadius: '8px', padding: '10px 12px', margin: '8px 0', fontSize: '13px', color: '#78350f',
              }}>
                ℹ️ Sẽ tự tạo {preview.new_type_codes.length} Loại đơn vị mới:{' '}
                <strong>{preview.new_type_codes.join(', ')}</strong> (tên mặc định = mã, đổi sau ở
                Cài đặt tổ chức → Loại đơn vị nếu muốn).
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table className="org-data-table">
                <thead>
                  <tr>
                    <th>Dòng</th>
                    <th>Mã đơn vị</th>
                    <th>Tên đơn vị</th>
                    <th>Mã loại</th>
                    <th>Mã cha</th>
                    <th>Kết quả</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row}>
                      <td>{r.row}</td>
                      <td>{r.code || '(rỗng)'}</td>
                      <td>{r.name}</td>
                      <td>{r.type_code}</td>
                      <td>{r.parent_code || '—'}</td>
                      {/* ISS-PILOT-15 (2026-08-09): dòng vẫn ✔ nhưng kèm chú thích
                          "+ tạo loại mới" khi type_code chưa có trong danh mục —
                          TA phải THẤY trước khi bấm Nhập, không âm thầm tự tạo. */}
                      <td style={r.ok ? impStyles.okCell : impStyles.badCell}>
                        {r.ok
                          ? `${r.action === 'update' ? '✔ Cập nhật' : '✔ Thêm'}${r.will_create_type ? ` (${r.reason})` : ''}`
                          : `✖ ${r.reason}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.errors > 0 && (
              <div style={impStyles.summary}>
                ⚠ {preview.errors} dòng lỗi (dòng {preview.error_rows.join(', ')}) sẽ bị bỏ qua;
                chỉ nhập {preview.valid} dòng hợp lệ.
              </div>
            )}
            <div style={impStyles.modalFoot}>
              <button type="button" className="org-btn org-btn-secondary" onClick={closePreview}>
                Hủy
              </button>
              <button
                type="button"
                className="org-btn org-btn-primary"
                onClick={handleImportCommit}
                disabled={committing || preview.valid === 0}
              >
                {committing ? 'Đang nhập…' : `Nhập ${preview.valid} dòng hợp lệ`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SC-028-BRANCH-GEOFENCE Điểm vào 1 — modal dùng chung với SC-003 tab
          "Vị trí chi nhánh" (BranchGeofenceTab). Đóng xong quay lại cây SC-006,
          KHÔNG điều hướng sang SC-003 (registry §SC-028 NAVIGATION). */}
      <BranchGeofenceModal
        target={geofenceTarget}
        onClose={() => setGeofenceTarget(null)}
        onSaved={() => setToast('Đã lưu vị trí chi nhánh')}
      />

      {/* Toast (pattern window.nqhToast) */}
      {toast && <div style={impStyles.toast}>{toast}</div>}
    </div>
  );
};

// ─── TreeTable (SC6-5: tree-table 5 cột thay bảng phẳng 7 cột) ────────────────

interface TreeTableProps {
  organizations: OrganizationEntity[];
  filteredIds: Set<string> | null;
  typeLabel: (org: OrganizationEntity) => string;
  unitCodeOf: (org: OrganizationEntity) => string;
  menuOpenId: string | null;
  setMenuOpenId: (id: string | null) => void;
  onAddChild: (org: OrganizationEntity) => void;
  onEdit: (org: OrganizationEntity) => void;
  onToggleStatus: (org: OrganizationEntity) => void;
  onDelete: (org: OrganizationEntity) => void;
  onConfigureGeofence: (org: OrganizationEntity) => void;
}

const TreeTable: React.FC<TreeTableProps> = ({
  organizations,
  filteredIds,
  typeLabel,
  unitCodeOf,
  menuOpenId,
  setMenuOpenId,
  onAddChild,
  onEdit,
  onToggleStatus,
  onDelete,
  onConfigureGeofence,
}) => {
  // F-7 fix: anchor rect cho PortalMenu (menu ⁝ không bị .org-data-table-container cắt)
  const [menuAnchor, setMenuAnchor] = useState<PortalMenuAnchor | null>(null);
  // Expand state — init: mở sẵn cấp gốc (registry DEFAULT: cấp 1–2 mở, cấp dưới gập)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(organizations.filter((o) => !o.parent_id).map((o) => o.id))
  );

  // Always-current ref so the effect below can read latest orgs without re-running on data changes.
  const orgsRef = useRef<OrganizationEntity[]>(organizations);
  orgsRef.current = organizations;

  // Chỉ react khi bật/tắt filter — giữ expand state khi refresh data (SAVED L983)
  useEffect(() => {
    if (filteredIds !== null) {
      // [1] tìm kiếm: tự mở nhánh chứa kết quả
      setExpandedIds(new Set(orgsRef.current.map((o) => o.id)));
    } else {
      setExpandedIds(new Set(orgsRef.current.filter((o) => !o.parent_id).map((o) => o.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredIds]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const childrenOf = useCallback(
    (parentId: string | null): OrganizationEntity[] =>
      organizations
        .filter((o) => (parentId === null ? !o.parent_id : o.parent_id === parentId))
        .sort((a, b) => (a.level || 1) - (b.level || 1) || a.name.localeCompare(b.name, 'vi')),
    [organizations]
  );

  // Flatten cây theo DFS, tôn trọng trạng thái gập/mở
  const visibleRows = useMemo(() => {
    const rows: Array<{ org: OrganizationEntity; depth: number; hasChildren: boolean }> = [];
    const walk = (parentId: string | null, depth: number) => {
      childrenOf(parentId).forEach((org) => {
        const children = childrenOf(org.id);
        const hasChildren = children.length > 0;
        rows.push({ org, depth, hasChildren });
        if (hasChildren && expandedIds.has(org.id)) walk(org.id, depth + 1);
      });
    };
    walk(null, 0);
    return rows;
  }, [childrenOf, expandedIds]);

  // Registry L964: "Thêm đơn vị con" ẩn nếu node đã ở cấp thấp nhất (v0 = Khu vực
  // làm việc ↔ work_area/level ≥ 6 trong data 8 lớp)
  const canAddChild = useCallback(
    (org: OrganizationEntity): boolean =>
      org.organization_type !== 'work_area' && (org.level || 1) < 6,
    []
  );

  if (visibleRows.length === 0) return null;

  return (
    <table className="org-data-table">
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>Tên đơn vị</th>
          <th style={{ textAlign: 'left', width: '140px' }}>Mã đơn vị</th>
          <th style={{ textAlign: 'left', width: '160px' }}>Loại</th>
          <th style={{ textAlign: 'left', width: '140px' }}>
            Trạng thái
            <PortalTooltip text="Đang hoạt động: dùng để xếp lịch và chấm công. Ngừng: tạm dừng, không nhận gán mới." tabIndex={0} ariaLabel="Đang hoạt động: dùng để xếp lịch và chấm công. Ngừng: tạm dừng, không nhận gán mới.">
              <InfoIcon />
            </PortalTooltip>
          </th>
          <th style={{ textAlign: 'center', width: '72px' }}>Thao tác</th>
        </tr>
      </thead>
      <tbody>
        {visibleRows.map(({ org, depth, hasChildren }) => {
          const isHighlighted = filteredIds !== null && filteredIds.has(org.id);
          const isDimmed = filteredIds !== null && !filteredIds.has(org.id);
          const isExpanded = expandedIds.has(org.id);
          return (
            <tr key={org.id} style={{ opacity: isDimmed ? 0.4 : 1 }}>
              {/* Tên đơn vị — thụt 2 space/cấp (D7) + ▾/▸; node lá không có toggle */}
              <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '320px' }}>
                <span style={{ paddingLeft: `${depth * 24}px`, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {hasChildren ? (
                    <button
                      type="button"
                      onClick={() => toggleExpand(org.id)}
                      aria-label={isExpanded ? 'Thu gọn' : 'Mở rộng'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '12px', color: 'var(--nqh-gray-500, #6b7280)' }}
                    >
                      {isExpanded ? '▾' : '▸'}
                    </button>
                  ) : (
                    <span style={{ display: 'inline-block', width: '16px' }} />
                  )}
                  <span
                    title={org.name}
                    style={{ fontWeight: isHighlighted ? 700 : depth === 0 ? 600 : 400 }}
                  >
                    {org.name}
                  </span>
                </span>
              </td>
              {/* Mã đơn vị — căn trái, bề rộng cố định */}
              <td><span className="org-code">{org.code}</span></td>
              {/* ISS-PILOT-8 (2026-08-09, Hang Le chốt): "Loại đơn vị" là cột THAM
                  CHIẾU (trỏ sang danh mục Loại đơn vị ở Cài đặt tổ chức, không phải
                  chủ thể chính của màn này) → format thống nhất "<Mã> • <Tên>"
                  (vd "BRANCH • Chi nhánh"), không chỉ hiện tên như trước.
                  ISS-PILOT-31 (2026-08-12, PO chốt): đổi dấu phân cách "-" → "•"
                  (U+2022) theo UI-UX-DECISION-REGISTER.md luật chuẩn <Mã> • <Tên>. */}
              <td>{unitCodeOf(org)} • {typeLabel(org)}</td>
              {/* Trạng thái — chip ●/○ (SC6-6: bỏ "⏳ Chờ duyệt") */}
              <td>
                <span style={{
                  padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 500,
                  background: org.is_active ? 'var(--nqh-herb-050, #dcfce7)' : 'var(--nqh-gray-100, #f3f4f6)',
                  color: org.is_active ? 'var(--nqh-herb-700, #16a34a)' : 'var(--nqh-gray-500, #6b7280)',
                }}>
                  {org.is_active ? '● Hoạt động' : '○ Ngừng'}
                </span>
              </td>
              {/* ⁝ menu từng node (registry L964) */}
              <td style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{ display: 'inline-block', position: 'relative' }} onMouseDown={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={(e) => {
                      if (menuOpenId === org.id) { setMenuOpenId(null); return; }
                      const r = e.currentTarget.getBoundingClientRect();
                      setMenuAnchor({ top: r.top, bottom: r.bottom, right: window.innerWidth - r.right });
                      setMenuOpenId(org.id);
                    }}
                    aria-label={`Thao tác cho ${org.name}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--nqh-gray-500, #6b7280)', padding: '2px 8px' }}
                  >
                    ⁝
                  </button>
                  {menuOpenId === org.id && menuAnchor && (
                    <PortalMenu anchor={menuAnchor} onClose={() => setMenuOpenId(null)}>
                      {canAddChild(org) && (
                        <button type="button" style={menuItemStyle} onClick={() => { setMenuOpenId(null); onAddChild(org); }}>
                          Thêm đơn vị con
                        </button>
                      )}
                      <button type="button" style={menuItemStyle} onClick={() => { setMenuOpenId(null); onEdit(org); }}>
                        Sửa
                      </button>
                      {/* SC-028-BRANCH-GEOFENCE Điểm vào 1 (registry §SC-028, DD-0099) —
                          chỉ node cấp Chi nhánh, các cấp khác không có geofence. */}
                      {org.organization_type === 'branch' && (
                        <button
                          type="button"
                          style={menuItemStyle}
                          onClick={() => { setMenuOpenId(null); onConfigureGeofence(org); }}
                        >
                          Cấu hình vị trí (Geofence)
                        </button>
                      )}
                      <button type="button" style={menuItemStyle} onClick={() => { setMenuOpenId(null); onToggleStatus(org); }}>
                        {org.is_active ? 'Ngừng hoạt động' : 'Kích hoạt'}
                      </button>
                      <button
                        type="button"
                        style={{ ...menuItemStyle, color: 'var(--nqh-chili, #dc2626)' }}
                        onClick={() => { setMenuOpenId(null); onDelete(org); }}
                      >
                        Xoá
                      </button>
                    </PortalMenu>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const menuItemStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 14px',
  background: 'white',
  border: 'none',
  cursor: 'pointer',
  fontSize: '13px',
  color: 'var(--nqh-gray-700, #374151)',
  whiteSpace: 'nowrap',
};

export default OrganizationManagement;
